"use client";

import TFLayoutHomeSection from "@/Web/Components/TFLayoutHomeSection/TFLayoutHomeSection";
import TFHorizontalSlider from "@/Web/Components/TFHorizontalSlider/TFHorizontalSlider";
import TFLink from "@/Web/Components/TFLink/TFLink";
import { useParams } from "next/navigation";
import { useState, useEffect } from "react";
import {
  IoGridSharp,
  IoTimeSharp,
  IoSettingsSharp,
  IoBookSharp,
  IoPeopleSharp,
  IoSparklesSharp,
} from "react-icons/io5";
import { useTranslations } from "next-intl";
import apiCall from "@/libs/api";

interface HubTopicCardProps {
  title: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  bgColor: string;
  iconColor: string;
}

const HubTopicCard = ({
  title,
  description,
  href,
  icon,
  bgColor,
  iconColor,
}: HubTopicCardProps) => {
  return (
    <TFLink
      href={href}
      className={`relative overflow-hidden rounded-2xl ${bgColor} flex flex-col h-full shadow-sm hover:brightness-95 transition-all duration-300 cursor-pointer`}
    >
      <div className="p-4 flex flex-col h-full">
        <div className="flex gap-4 items-center flex-grow">
          <div className={`flex-shrink-0 ${iconColor} self-center`}>{icon}</div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-black mb-2.5 leading-tight">
              {title}
            </h3>

            <p className="text-sm text-black/65 leading-relaxed line-clamp-3">
              {description}
            </p>
          </div>
        </div>
      </div>
    </TFLink>
  );
};

const getFeatureIcon = (ref: string) => {
  const iconMap: Record<string, React.ReactNode> = {
    "unified-center": <IoGridSharp className="w-12 h-12" />,
    sessions: <IoTimeSharp className="w-12 h-12" />,
    integration: <IoSettingsSharp className="w-12 h-12" />,
    library: <IoBookSharp className="w-12 h-12" />,
    collaboration: <IoPeopleSharp className="w-12 h-12" />,
    "ai-assistant": <IoSparklesSharp className="w-12 h-12" />,
  };
  return iconMap[ref] || <IoGridSharp className="w-12 h-12" />;
};

const getFeatureStyles = (ref: string) => {
  const styleMap: Record<string, { bgColor: string; iconColor: string }> = {
    "unified-center": { bgColor: "bg-blue-100", iconColor: "text-blue-600" },
    sessions: { bgColor: "bg-purple-100", iconColor: "text-purple-600" },
    integration: { bgColor: "bg-green-100", iconColor: "text-green-600" },
    library: { bgColor: "bg-amber-100", iconColor: "text-amber-600" },
    collaboration: { bgColor: "bg-cyan-100", iconColor: "text-cyan-600" },
    "ai-assistant": { bgColor: "bg-pink-100", iconColor: "text-pink-600" },
  };
  return (
    styleMap[ref] || { bgColor: "bg-blue-100", iconColor: "text-blue-600" }
  );
};

export default function SectionHub() {
  const t = useTranslations("web.pages.home.sections.hub");
  const [itemsPerView, setItemsPerView] = useState(3);
  const [features, setFeatures] = useState<APIDataHubFeature[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const updateItemsPerView = () => {
      setItemsPerView(window.innerWidth < 768 ? 1 : 3);
    };

    updateItemsPerView();
    window.addEventListener("resize", updateItemsPerView);
    return () => window.removeEventListener("resize", updateItemsPerView);
  }, []);

  const params = useParams<{ locale: string }>();
  const locale = params?.locale || "ru";

  useEffect(() => {
    const loadFeatures = async () => {
      try {
        setIsLoading(true);
        const response = await apiCall("hub/features/list", {
          locale: locale as UILocale,
          status: "published",
        });
        setFeatures(response.features);
      } catch (error) {
        console.error("Failed to load features:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (locale) {
      loadFeatures();
    }
  }, [locale]);

  if (isLoading || features.length === 0) {
    return null;
  }

  const hubTopics = features.map((feature) => {
    const styles = getFeatureStyles(feature.ref);
    return {
      ...feature,
      href: `/platform/hub/features/${feature.ref}`,
      icon: getFeatureIcon(feature.ref),
      bgColor: styles.bgColor,
      iconColor: styles.iconColor,
    };
  });

  return (
    <TFLayoutHomeSection
      title={t("title")}
      description={t("description")}
      moreButton={{
        title: t("learnMore"),
        href: `/platform/hub/features`,
      }}
    >
      <TFHorizontalSlider
        items={hubTopics}
        renderItem={(topic) => (
          <HubTopicCard
            title={topic.title}
            description={topic.description}
            href={topic.href}
            icon={topic.icon}
            bgColor={topic.bgColor}
            iconColor={topic.iconColor}
          />
        )}
        itemWidth={320}
        gap={16}
        autoScroll={false}
        showControls={true}
        itemsPerView={itemsPerView}
        showDots={true}
      />
    </TFLayoutHomeSection>
  );
}
